<script setup lang="ts">
definePageMeta({ layout: false })

useHead({ title: 'Design System Review' })

const modalOpen = ref(false)

const colorSwatches = [
  { hex: '#FFEA00', name: 'Safety Yellow', note: 'Attention. Fills, highlights, the loudest sticker.', rgb: '255, 234, 0', textOnFill: 'var(--color-brand-ink)', token: '--color-brand-yellow' },
  { hex: '#FF2D2D', name: 'Punch Red', note: 'Shadow offset, flame disk, alerts. Accent only.', rgb: '255, 45, 45', textOnFill: 'var(--color-brand-chalk)', token: '--color-brand-red' },
  { hex: '#111111', name: 'Deck Black', note: 'Body text, borders, dark sheet surfaces.', rgb: '17, 17, 17', textOnFill: 'var(--color-brand-chalk)', token: '--color-brand-ink' },
  { hex: '#FBF8F1', name: 'Chalk', note: 'Paper. Library background, card body, body text on dark.', rgb: '251, 248, 241', textOnFill: 'var(--color-brand-ink)', token: '--color-brand-chalk' },
] as const

const typeSpecimens = [
  { cls: 'font-editorial italic font-black text-hero', name: 'Hero', sample: 'Games that get the room together.' },
  { cls: 'font-editorial italic font-black text-display', name: 'Display', sample: 'Free games for youth groups.' },
  { cls: 'font-display text-sticker-lg', name: 'Sticker LG', sample: 'PLAY FREE' },
  { cls: 'font-display text-sticker-md', name: 'Sticker MD', sample: 'TEAM SHOWDOWN' },
  { cls: 'font-display text-sticker-sm', name: 'Sticker SM', sample: 'NO SIGN UP' },
  { cls: 'font-display uppercase text-eyebrow tracking-[0.18em]', name: 'Eyebrow', sample: 'SIGNAL FIRE GAMES' },
  { cls: 'font-body text-body', name: 'Body', sample: 'Pick one, open your screen, play. No sign-up. Works on any device.' },
  { cls: 'font-body text-body-sm font-medium', name: 'Body SM', sample: 'Runs in the browser on phones, tablets, laptops.' },
  { cls: 'font-body font-extrabold uppercase text-label tracking-[0.14em]', name: 'Label', sample: '6 TO 30 KIDS / 10 MIN' },
]

const voiceSamples = [
  {
    bad: 'Ignite your youth group with epic, faith-infused games that will transform your Wednesday nights!',
    context: 'Hero copy',
    good: 'Free games for the people running youth group. Pick one, open your screen, play.',
  },
  {
    bad: 'Let\'s get this party started!',
    context: 'Empty state',
    good: 'Pick two mini-games to start the wheel.',
  },
  {
    bad: 'Oops! Something went wrong.',
    context: 'Error message',
    good: 'The pairing code didn\'t go through. Try again or regenerate a new one.',
  },
  {
    bad: 'Signal Fire Games is passionate about empowering youth ministries to engage the next generation.',
    context: 'About us',
    good: 'We build simple games for Christian youth groups. The site is free. Kaweah Tech hosts it and keeps adding to it when we can.',
  },
]
</script>

<template>
  <div class="min-h-screen bg-brand-chalk text-brand-ink">
    <div class="mx-auto max-w-7xl px-6 py-16 sm:px-10 sm:py-24">
      <!-- ===== 01. Masthead ===== -->
      <header class="mb-24">
        <div class="flex items-center gap-3 mb-6">
          <SfgEyebrow>Kaweah Tech · Signal Fire Games</SfgEyebrow>
          <span class="font-display uppercase text-eyebrow tracking-[0.18em] text-brand-ink/60">v1 · 2026-04-24</span>
        </div>
        <SfgHeading
          :level="1"
          highlight="review"
          highlight-color="yellow"
        >
          Design system
          <template #after>
            .
          </template>
        </SfgHeading>
        <p class="mt-8 max-w-2xl font-body text-body">
          Temporary review page. Every token, every branded component, every Nuxt UI override, and the reserved
          Raspberry Limeade alt theme, all demonstrated in situ. Delete or move behind a dev flag once the site
          restructure begins.
        </p>
      </header>

      <!-- ===== 02. Colors ===== -->
      <SfgReviewSection
        n="02"
        title="Colors"
        eyebrow="Four colors. No gray."
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div
            v-for="c in colorSwatches"
            :key="c.token"
            class="rounded-card border-[1.5px] border-brand-ink overflow-hidden"
          >
            <div
              class="h-32 flex items-end p-4"
              :style="{ background: `var(${c.token})`, color: c.textOnFill }"
            >
              <span class="font-display text-sticker-md">{{ c.hex }}</span>
            </div>
            <div class="p-4 bg-brand-chalk border-t-[1.5px] border-brand-ink">
              <div class="font-display text-[15px] uppercase tracking-[0.08em]">
                {{ c.name }}
              </div>
              <div class="font-body text-[11px] opacity-70 mt-1">
                rgb({{ c.rgb }})
              </div>
              <p class="font-body text-body-sm mt-2">
                {{ c.note }}
              </p>
              <code class="block mt-2 font-mono text-[11px] opacity-70">{{ c.token }}</code>
            </div>
          </div>
        </div>
      </SfgReviewSection>

      <!-- ===== 03. Typography ===== -->
      <SfgReviewSection
        n="03"
        title="Typography"
        eyebrow="Bungee, Bungee Shade, Fraunces, Work Sans."
      >
        <div class="space-y-6">
          <div
            v-for="t in typeSpecimens"
            :key="t.name"
            class="grid grid-cols-[120px_1fr] gap-6 items-baseline py-4 border-b border-brand-line"
          >
            <div class="font-display uppercase text-eyebrow tracking-[0.18em] opacity-70">
              {{ t.name }}
            </div>
            <div :class="t.cls">
              {{ t.sample }}
            </div>
          </div>
          <div class="grid grid-cols-[120px_1fr] gap-6 items-baseline py-4 border-b border-brand-line">
            <div class="font-display uppercase text-eyebrow tracking-[0.18em] opacity-70">
              Display Alt
            </div>
            <div class="font-display-alt text-[56px] leading-none">
              BUNGEE SHADE
            </div>
          </div>
        </div>
      </SfgReviewSection>

      <!-- ===== 04. Logo system ===== -->
      <SfgReviewSection
        n="04"
        title="Logo system"
        eyebrow="Sticker, flame, and two compact lockups."
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div class="flex flex-col items-start gap-4 p-10 rounded-card border-[1.5px] border-brand-ink">
            <SfgWordmark variant="sticker"/>
            <code class="font-mono text-[11px] opacity-70">variant=&quot;sticker&quot;</code>
          </div>
          <div class="flex flex-col items-start gap-4 p-10 rounded-card border-[1.5px] border-brand-ink">
            <SfgWordmark variant="flame"/>
            <code class="font-mono text-[11px] opacity-70">variant=&quot;flame&quot;</code>
          </div>
          <div class="flex flex-col items-start gap-4 p-6 rounded-card sfg-sheet-texture">
            <SfgWordmark variant="compact-dark"/>
            <code class="font-mono text-[11px] text-brand-chalk/70">variant=&quot;compact-dark&quot;</code>
          </div>
          <div class="flex flex-col items-start gap-4 p-6 rounded-card border-[1.5px] border-brand-ink">
            <SfgWordmark variant="compact-red"/>
            <code class="font-mono text-[11px] opacity-70">variant=&quot;compact-red&quot;</code>
          </div>
        </div>
      </SfgReviewSection>

      <!-- ===== 05. Sticker primitives ===== -->
      <SfgReviewSection
        n="05"
        title="Sticker primitives"
        eyebrow="Banner, rosette, bar, flame, star."
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10">
          <div class="sfg-sheet-texture rounded-card p-12 flex flex-wrap gap-10 items-center justify-center">
            <SfgStickerBanner
              top-line="SPIN"
              bottom-line="THE WHEEL"
              fill="yellow"
              :tilt="1"
            />
            <SfgStickerBanner
              top-line="ROUND"
              bottom-line="ONE"
              fill="red"
              :tilt="4"
            />
          </div>
          <div class="sfg-sheet-texture rounded-card p-12 flex flex-wrap gap-10 items-center justify-center">
            <SfgStickerRosette
              top="PLAY"
              middle="NOW"
              bottom="FREE"
              fill="ink"
              :tilt="3"
            />
            <SfgStickerRosette
              top="ANY"
              middle="DEVICE"
              bottom="ANY ROOM"
              fill="yellow"
              :tilt="1"
            />
          </div>
        </div>

        <div class="flex flex-wrap gap-6 items-center mb-4">
          <SfgStickerBar text="NO SIGN UP" :tilt="2"/>
          <SfgStickerBar
            text="WORKS ON ANY DEVICE"
            fill="yellow"
            :tilt="4"
          />
          <SfgStickerBar
            text="FREE TO RUN"
            fill="red"
            :tilt="1"
          />
        </div>

        <!-- Pill variant: wider two-word badges without circular overflow -->
        <div class="flex flex-wrap gap-6 items-center mb-10">
          <SfgStickerPill label="SHOWDOWN" :tilt="2"/>
          <SfgStickerPill
            label="TEAM RELAY"
            fill="yellow"
            :tilt="3"
          />
          <SfgStickerPill
            label="NAME THAT VERSE"
            fill="red"
            :tilt="1"
          />
        </div>

        <!-- Flame on a disk: the circular badge -->
        <div class="sfg-sheet-texture rounded-card p-12 flex flex-wrap gap-12 items-center justify-center mb-4">
          <SfgFlame
            :size="140"
            bg-color="red"
            flame-color="yellow"
            :tilt="2"
          />
          <SfgFlame
            :size="140"
            bg-color="yellow"
            body-color="red"
            flame-color="red"
            :tilt="4"
          />
          <SfgFlame
            :size="140"
            bg-color="chalk"
            body-color="red"
            flame-color="yellow"
            :tilt="1"
          />
        </div>

        <!-- Flame without a disk: the two-layer hero mark -->
        <div class="bg-brand-chalk rounded-card border-2 border-brand-ink p-12 flex flex-wrap gap-12 items-center justify-center mb-4">
          <span class="font-display uppercase text-[10px] tracking-[0.18em] w-full text-center text-brand-ink/60 mb-2">
            disk = false · hero lockup treatment
          </span>
          <SfgFlame
            :size="180"
            :disk="false"
            body-color="red"
            flame-color="yellow"
            :tilt="1"
          />
          <SfgFlame
            :size="180"
            :disk="false"
            body-color="ink"
            flame-color="yellow"
            :tilt="2"
          />
          <SfgFlame
            :size="180"
            :disk="false"
            body-color="red"
            flame-color="chalk"
            :tilt="4"
          />
        </div>

        <!-- Stars -->
        <div class="sfg-sheet-texture rounded-card p-12 flex flex-wrap gap-12 items-center justify-center mb-4">
          <SfgStar :size="120" :tilt="1"/>
          <SfgStar
            :size="120"
            fill="red"
            stroke="ink"
            :tilt="4"
          />
        </div>

        <!-- Shape stickers: text-free graphic accents -->
        <div class="bg-brand-chalk rounded-card border-2 border-brand-ink p-12 flex flex-wrap gap-10 items-center justify-center">
          <span class="font-display uppercase text-[10px] tracking-[0.18em] w-full text-center text-brand-ink/60 mb-2">
            Shape stickers · text-free accents for composing hero surfaces
          </span>
          <SfgStarburst :size="72" :tilt="-6"/>
          <SfgLightning1 :size="80" :tilt="-10"/>
          <SfgLightning2 :size="92" :tilt="8"/>
          <SfgHeart :size="68" :tilt="-4"/>
        </div>
      </SfgReviewSection>

      <!-- ===== 06. Buttons ===== -->
      <SfgReviewSection
        n="06"
        title="Buttons"
        eyebrow="One primary, one ghost, Nuxt UI underneath."
      >
        <div class="flex flex-wrap gap-4 mb-8">
          <SfgButton variant="primary" size="sm">
            Start a game
          </SfgButton>
          <SfgButton variant="primary" size="md">
            Start a game
          </SfgButton>
          <SfgButton variant="primary" size="lg">
            Start a game
          </SfgButton>
        </div>

        <div class="sfg-sheet-texture rounded-card p-8 flex flex-wrap gap-4 mb-8">
          <SfgButton variant="ghost" size="sm">
            See the library
          </SfgButton>
          <SfgButton variant="ghost" size="md">
            See the library
          </SfgButton>
          <SfgButton variant="ghost" size="lg">
            See the library
          </SfgButton>
        </div>

        <div class="flex flex-wrap gap-4">
          <UButton color="primary" variant="solid">
            UButton primary
          </UButton>
          <UButton color="secondary" variant="solid">
            UButton secondary
          </UButton>
          <UButton color="primary" variant="ghost">
            UButton ghost
          </UButton>
        </div>
      </SfgReviewSection>

      <!-- ===== 07. Nuxt UI components ===== -->
      <SfgReviewSection
        n="07"
        title="Nuxt UI primitives"
        eyebrow="Theme-overridden. Drop-in usage."
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <UCard>
            <template #header>
              <div class="font-display uppercase text-sticker-sm tracking-[0.08em]">
                UCard header
              </div>
            </template>
            <p class="font-body text-body">
              Default UCard. Chalk body, 2px ink border, 6px radius, 5px ink drop shadow. Same sticker treatment as the rest of the system so the card reads like every other surface, not as an admin panel.
            </p>
            <template #footer>
              <div class="flex gap-2">
                <SfgButton variant="primary" size="sm">
                  Action
                </SfgButton>
                <SfgButton variant="ghost" size="sm">
                  Cancel
                </SfgButton>
              </div>
            </template>
          </UCard>

          <div class="flex flex-col gap-4">
            <div class="flex flex-wrap gap-2">
              <SfgBadge variant="live">
                LIVE
              </SfgBadge>
              <SfgBadge variant="soon">
                SOON
              </SfgBadge>
              <SfgBadge variant="alert">
                ALERT
              </SfgBadge>
              <SfgBadge variant="new">
                NEW
              </SfgBadge>
            </div>

            <UInput placeholder="Pairing code" size="md"/>
            <UInput placeholder="Email" size="md"/>

            <div class="flex gap-2">
              <SfgButton
                variant="primary"
                size="md"
                @click="modalOpen = true"
              >
                Open modal
              </SfgButton>
            </div>

            <UModal v-model:open="modalOpen">
              <template #header>
                <div class="font-editorial italic font-black text-[28px]">
                  What is Signal Fire Games?
                </div>
              </template>
              <template #body>
                <p class="font-body text-body">
                  We build simple games for Christian youth groups. The site is free. Kaweah Tech hosts it,
                  supports it, and keeps adding to it when we can.
                </p>
              </template>
              <template #footer>
                <SfgButton
                  variant="primary"
                  size="sm"
                  @click="modalOpen = false"
                >
                  Got it
                </SfgButton>
              </template>
            </UModal>
          </div>
        </div>
      </SfgReviewSection>

      <!-- ===== 08. Voice samples ===== -->
      <SfgReviewSection
        n="08"
        title="Voice"
        eyebrow="Don't vs do. Lifted from the voice foundation."
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            v-for="v in voiceSamples"
            :key="v.context"
            class="rounded-card border-[1.5px] border-brand-ink overflow-hidden"
          >
            <div class="px-4 py-2 bg-brand-ink text-brand-chalk font-display uppercase text-[11px] tracking-[0.14em]">
              {{ v.context }}
            </div>
            <div class="p-4 border-b border-brand-line">
              <div class="font-display uppercase text-[10px] tracking-[0.18em] text-brand-red">
                Don&#39;t
              </div>
              <p class="font-body text-body-sm mt-1 opacity-70 line-through">
                {{ v.bad }}
              </p>
            </div>
            <div class="p-4">
              <div class="font-display uppercase text-[10px] tracking-[0.18em] text-brand-ink">
                Do
              </div>
              <p class="font-body text-body mt-1">
                {{ v.good }}
              </p>
            </div>
          </div>
        </div>
      </SfgReviewSection>

      <!-- ===== 09. Applied patterns ===== -->
      <SfgReviewSection
        n="09"
        title="Applied patterns"
        eyebrow="The real brand surfaces, in situ."
      >
        <div class="space-y-10">
          <!-- Homepage hero -->
          <SfgMock label="signalfire.games · /">
            <SfgSheet :padded="false">
              <div class="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-10 px-10 py-16 items-center">
                <div>
                  <div class="mb-6">
                    <SfgWordmark variant="compact-dark"/>
                  </div>
                  <SfgHeading
                    :level="1"
                    highlight="room"
                    highlight-color="yellow"
                    class="!text-brand-chalk"
                  >
                    Games that get the
                    <template #after>
                      together.
                    </template>
                  </SfgHeading>
                  <p class="font-body text-body mt-6 text-brand-chalk max-w-lg">
                    Free, web-based games for Christian youth groups. Pick one, open your screen, play. No sign-up.
                    Works on any device.
                  </p>
                  <div class="flex gap-4 mt-8">
                    <SfgButton variant="primary" size="md">
                      Start a game
                    </SfgButton>
                    <SfgButton variant="ghost" size="md">
                      See the library
                    </SfgButton>
                  </div>
                </div>
                <div class="relative h-[320px]">
                  <div class="absolute top-0 right-8">
                    <SfgStar :size="80" :tilt="4"/>
                  </div>
                  <div class="absolute top-16 left-4">
                    <SfgStickerBanner
                      top-line="SPIN"
                      bottom-line="THE WHEEL"
                      fill="yellow"
                      :tilt="1"
                    />
                  </div>
                  <div class="absolute bottom-8 right-0">
                    <SfgFlame
                      :size="120"
                      bg-color="red"
                      flame-color="yellow"
                      :tilt="3"
                    />
                  </div>
                  <div class="absolute bottom-0 left-12">
                    <SfgStickerBar
                      text="NO SIGN UP"
                      fill="chalk"
                      :tilt="4"
                    />
                  </div>
                </div>
              </div>
            </SfgSheet>
          </SfgMock>

          <!-- Game library grid -->
          <SfgMock label="signalfire.games · /games">
            <SfgPaper :padded="false">
              <div class="px-10 py-14">
                <SfgEyebrow variant="outline">
                  Library
                </SfgEyebrow>
                <SfgHeading :level="2" class="mt-4 mb-8">
                  Every game in the box.
                </SfgHeading>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <SfgGameTile
                    title="Showdown"
                    format="Teams"
                    :type="['Skills', 'Knowledge']"
                    duration="10–30 min"
                    player-note="6+ recommended"
                    status="live"
                    sticker-top="TEAM"
                    sticker-bottom="SHOWDOWN"
                    art-fill="yellow"
                  />
                  <SfgGameTile
                    title="Team Relay"
                    format="Teams"
                    :type="['Skills']"
                    duration="15–25 min"
                    player-note="8+ recommended"
                    status="soon"
                    sticker-top="TEAM"
                    sticker-bottom="RELAY"
                    art-fill="red"
                  />
                  <SfgGameTile
                    title="Name That Verse"
                    format="Individual"
                    :type="['Knowledge']"
                    duration="5–15 min"
                    status="soon"
                    sticker-top="NAME"
                    sticker-bottom="THAT VERSE"
                    art-fill="yellow"
                  />
                </div>
              </div>
            </SfgPaper>
          </SfgMock>

          <!-- Game detail layout -->
          <SfgMock label="signalfire.games · /games/showdown">
            <SfgSheet :padded="false">
              <div class="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-10 px-10 py-16 items-start">
                <div>
                  <SfgEyebrow variant="inverted">
                    Game
                  </SfgEyebrow>
                  <SfgHeading :level="1" class="!text-brand-chalk mt-4">
                    Showdown.
                  </SfgHeading>
                  <p class="font-body text-body mt-4 max-w-lg">
                    Spin the wheel. Whatever lands, the group plays. Works on any screen, no sign-up, and you can
                    hand the remote to a leader or cast to the room.
                  </p>
                  <div class="flex gap-3 mt-8">
                    <SfgButton variant="primary" size="md">
                      Start a game
                    </SfgButton>
                    <SfgButton variant="ghost" size="md">
                      How it runs
                    </SfgButton>
                  </div>
                </div>
                <aside class="bg-brand-chalk text-brand-ink rounded-card p-6">
                  <div class="font-display uppercase text-[11px] tracking-[0.18em] text-brand-red">
                    How it runs
                  </div>
                  <ol class="mt-4 space-y-3 font-body text-body">
                    <li><strong>1.</strong> Open the site on the room screen.</li>
                    <li><strong>2.</strong> Scan the pairing code from a phone.</li>
                    <li><strong>3.</strong> Pick two or more mini-games.</li>
                    <li><strong>4.</strong> Spin. Play. Hand off.</li>
                  </ol>
                </aside>
              </div>
            </SfgSheet>
          </SfgMock>

          <!-- 404 -->
          <SfgMock label="signalfire.games · /not-a-page">
            <div class="bg-brand-red text-brand-chalk relative px-10 py-20 text-center overflow-hidden">
              <div class="absolute top-8 left-8">
                <SfgStar
                  :size="80"
                  :tilt="4"
                  fill="yellow"
                />
              </div>
              <div class="absolute top-12 right-10">
                <SfgStar
                  :size="60"
                  :tilt="1"
                  fill="chalk"
                />
              </div>
              <div class="font-display-alt text-[160px] leading-none">
                404
              </div>
              <h3 class="font-display uppercase text-sticker-md tracking-[0.08em] mt-4">
                This page rolled off the table.
              </h3>
              <p class="font-body text-body mt-3 max-w-xl mx-auto">
                The link you followed doesn't exist (anymore). Head back to the library, or start a game.
              </p>
              <div class="mt-8 flex justify-center">
                <SfgButton variant="primary" size="md">
                  Back to the library
                </SfgButton>
              </div>
            </div>
          </SfgMock>
        </div>
      </SfgReviewSection>

      <!-- ===== 10. Alt theme preview ===== -->
      <SfgReviewSection
        n="10"
        title="Raspberry Limeade"
        eyebrow="Alt theme. Declared, not wired."
      >
        <p class="font-body text-body mb-8 max-w-2xl">
          The theme picker is intentionally not built yet. Every element below sits inside a
          <code class="font-mono text-[13px] px-1 py-0.5 bg-brand-ink text-brand-chalk rounded-[3px]">data-theme=&quot;raspberry&quot;</code>
          wrapper. Nothing else changed. The tokens swap underneath.
        </p>
        <div data-theme="raspberry" class="rounded-card overflow-hidden border-[1.5px] border-brand-ink">
          <SfgSheet :padded="false">
            <div class="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] gap-10 px-10 py-14 items-center">
              <div>
                <SfgEyebrow variant="inverted">
                  Alt theme preview
                </SfgEyebrow>
                <SfgHeading
                  :level="2"
                  highlight="limeade"
                  class="!text-brand-chalk mt-4"
                >
                  Raspberry
                  <template #after>
                    .
                  </template>
                </SfgHeading>
                <p class="font-body text-body mt-4 max-w-md">
                  Same sticker, same button, same tile. Palette swapped by a single attribute.
                </p>
                <div class="flex gap-3 mt-6">
                  <SfgButton variant="primary" size="md">
                    Start a game
                  </SfgButton>
                  <SfgButton variant="ghost" size="md">
                    See the library
                  </SfgButton>
                </div>
              </div>
              <div class="flex flex-wrap gap-8 justify-center">
                <SfgStickerBanner
                  top-line="SPIN"
                  bottom-line="THE WHEEL"
                  fill="yellow"
                  :tilt="1"
                />
                <SfgFlame
                  :size="120"
                  bg-color="red"
                  flame-color="yellow"
                  :tilt="3"
                />
              </div>
            </div>
          </SfgSheet>
          <SfgPaper :padded="false">
            <div class="p-10">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <SfgGameTile
                  title="Showdown"
                  format="Teams"
                  :type="['Skills', 'Knowledge']"
                  duration="10–30 min"
                  player-note="6+ recommended"
                  status="live"
                  sticker-top="TEAM"
                  sticker-bottom="SHOWDOWN"
                  art-fill="yellow"
                />
                <SfgGameTile
                  title="Team Relay"
                  format="Teams"
                  :type="['Skills']"
                  duration="15–25 min"
                  player-note="8+ recommended"
                  status="soon"
                  sticker-top="TEAM"
                  sticker-bottom="RELAY"
                  art-fill="red"
                />
              </div>
            </div>
          </SfgPaper>
        </div>
      </SfgReviewSection>

      <footer class="pt-16 border-t border-brand-line">
        <p class="font-display uppercase text-eyebrow tracking-[0.18em] opacity-60">
          End of review. Token layer ready, no content migrated, no pages touched outside the design route.
        </p>
      </footer>
    </div>
  </div>
</template>
